// src/features/events/backupSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { backend_url } from "../../App";
import { setSnackBar } from "../../reusable_components/site_data/siteDataSlice";

// ─────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────

export const fetchBackupSettings = createAsyncThunk(
  "backup/fetchBackupSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${backend_url}/core/api/backup-settings/1/`,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const updateBackupSettings = createAsyncThunk(
  "backup/updateBackupSettings",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.patch(
        `${backend_url}/core/api/backup-settings/1/`,
        payload,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      dispatch(
        setSnackBar({
          message: "Settings saved successfully",
          severity: "success",
        }),
      );
      return res.data;
    } catch (err) {
      dispatch(
        setSnackBar({ message: "Failed to save settings", severity: "error" }),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

// Background Polling
const pollUntilDone = (
  dispatch,
  { maxAttempts = 720, intervalMs = 10000 } = {},
) => {
  return new Promise((resolve) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(
          `${backend_url}/core/api/event/?page_size=1&is_backed_up=false`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        if (res.data.count === 0) return resolve({ ok: true });
        if (attempts >= maxAttempts)
          return resolve({ ok: false, timeout: true });
        setTimeout(tick, intervalMs);
      } catch (e) {
        setTimeout(tick, intervalMs);
      }
    };
    tick();
  });
};

export const triggerBackup = createAsyncThunk(
  "backup/triggerBackup",
  async (deleteUntagged, { rejectWithValue, dispatch }) => {
    try {
      const endpoint = deleteUntagged
        ? "backup_and_delete_events"
        : "backup_events";
      const res = await axios.post(
        `${backend_url}/core/api/event/${endpoint}/`,
        {},
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );

      if (res.status !== 202) return rejectWithValue(res.data);

      dispatch(
        setSnackBar({
          message: "🔄 Backup started in background...",
          severity: "info",
        }),
      );
      const result = await pollUntilDone(dispatch);

      if (result.ok)
        dispatch(
          setSnackBar({ message: "✅ Backup completed!", severity: "success" }),
        );
      else
        dispatch(
          setSnackBar({
            message: "⚠️ Backup still running in background.",
            severity: "warning",
          }),
        );

      return result;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  },
);

const backupSlice = createSlice({
  name: "backup",
  initialState: {
    settings: null,
    running: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBackupSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(updateBackupSettings.fulfilled, (state, action) => {
        state.settings = { ...state.settings, ...action.payload };
      })
      .addCase(triggerBackup.pending, (state) => {
        state.running = true;
      })
      .addCase(triggerBackup.fulfilled, (state) => {
        state.running = false;
      })
      .addCase(triggerBackup.rejected, (state) => {
        state.running = false;
      });
  },
});

export const selectBackupSettings = (state) => state.backup.settings;
export const selectBackupRunning = (state) => state.backup.running;
export default backupSlice.reducer;
